import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout.jsx';
import { api, uploadUrl } from '../api/client.js';

const FALLBACK_IMAGES = {
  school: 'https://picsum.photos/seed/exoticpet-school/1200/800',
  expo: 'https://picsum.photos/seed/exoticpet-expo/1200/800',
  contest: 'https://picsum.photos/seed/exoticpet-contest/1200/800',
  other: 'https://picsum.photos/seed/exoticpet/1200/800',
};
const KATEGORI_LABEL = { school: 'School Safari', expo: 'Expo', contest: 'Contest', other: 'Lainnya' };

function resolveImage(item) {
  if (item?.image) return uploadUrl(item.image);
  return FALLBACK_IMAGES[item?.kategori] || FALLBACK_IMAGES.other;
}

function handleImageError(e) {
  e.target.onerror = null;
  e.target.src = 'https://picsum.photos/seed/exoticpet/800/600';
}

function formatDateFull(tanggal) {
  if (!tanggal) return '';
  return new Date(tanggal).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatDateShort(tanggal) {
  if (!tanggal) return '';
  return new Date(tanggal).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Konten dari CKEditor sudah HTML. Untuk data lama yang masih plain text, bungkus jadi paragraf.
function renderableHtml(konten) {
  if (!konten) return '';
  const hasTags = /<[a-z][\s\S]*>/i.test(konten);
  if (hasTags) return konten;
  const paragraphs = konten
    .split(/\r\n|\r|\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return paragraphs.map((p) => `<p>${escape(p)}</p>`).join('');
}

export default function ArtikelPage() {
  const { id } = useParams();
  const [item, setItem] = useState(undefined); // undefined = loading, null = not found
  const [related, setRelated] = useState([]);
  const [showContent, setShowContent] = useState(false);
  const [galleryImages, setGalleryImages] = useState(undefined);

  useEffect(() => {
    setItem(undefined);
    setShowContent(false);
    setGalleryImages(undefined);
    api.get(`/portfolio/${id}`)
      .then(({ data }) => {
        if (!data.item.konten_artikel) {
          setItem(null);
          return;
        }
        setItem(data.item);
        api.get(`/portfolio/${id}/images`).then(({ data: gd }) => setGalleryImages(gd.items || [])).catch(() => setGalleryImages([]));
        api.get(`/portfolio/${id}/related`).then(({ data: rd }) => setRelated(rd.items || [])).catch(() => setRelated([]));
      })
      .catch(() => setItem(null));
  }, [id]);

  useEffect(() => {
    if (item) {
      const t = setTimeout(() => setShowContent(true), 400);
      return () => clearTimeout(t);
    }
  }, [item]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <PublicLayout>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-40 md:pb-24">
        <div className="mb-6 hidden md:block">
          <Link
            to="/portofolio"
            className="inline-flex items-center gap-1.5 text-sm font-semibold transition-all duration-300 ease-in-out px-3 py-1.5 rounded-full text-neutral-600 dark:text-neutral-300 hover:bg-neutral-800/20 dark:hover:bg-white/10 hover:text-forest-600 dark:hover:text-white hover:rounded-xl active:scale-95"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            Kembali ke Portofolio
          </Link>
        </div>

        {item === undefined && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <ArticleSkeleton />
            </div>
            <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
              <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-40 rounded-2xl mb-6" />
              <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-56 rounded-2xl" />
            </div>
          </div>
        )}

        {item === null && (
          <div className="text-center py-20">
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">Artikel Tidak Ditemukan</h2>
            <Link to="/portofolio" className="text-forest-500 font-semibold hover:underline">Kembali ke Portofolio</Link>
          </div>
        )}

        {item && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {!showContent ? (
                <ArticleSkeleton />
              ) : (
                <ArticleContent item={item} shareUrl={shareUrl} galleryImages={galleryImages} />
              )}
            </div>

            <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
              <ShareCard item={item} shareUrl={shareUrl} />
              <RelatedCard related={related} />
            </div>
          </div>
        )}
      </main>
    </PublicLayout>
  );
}

function ArticleSkeleton() {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 sm:p-6 shadow-sm border border-neutral-100 dark:border-neutral-800 space-y-6">
      <div>
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-5 w-24 rounded-full mb-3" />
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-9 w-3/4 rounded-xl mb-3" />
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-9 w-1/2 rounded-xl mb-4" />
        <div className="flex gap-4">
          <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-4 w-32 rounded-lg" />
          <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-4 w-24 rounded-lg" />
        </div>
      </div>
      <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 aspect-[20/9] w-full rounded-xl" />
      <div className="space-y-3 pt-2">
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-4 w-full rounded-lg" />
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-4 w-full rounded-lg" />
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-4 w-11/12 rounded-lg" />
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-4 w-10/12 rounded-lg mb-6" />
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-4 w-full rounded-lg" />
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-4 w-full rounded-lg" />
        <div className="animate-pulse bg-neutral-200 dark:bg-neutral-700/60 h-4 w-9/12 rounded-lg" />
      </div>
    </div>
  );
}

function ArticleContent({ item, shareUrl, galleryImages }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const cat = item.kategori || 'other';
  const img = resolveImage(item);
  const dateFull = formatDateFull(item.tanggal);
  const kontenHtml = renderableHtml(item.konten_artikel);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    return uploadUrl(imagePath);
  };

  return (
    <>
      <article className="bg-white dark:bg-neutral-900 rounded-2xl p-5 sm:p-6 shadow-sm border border-neutral-100 dark:border-neutral-800 opacity-0 animate-[artikelFadeIn_0.5s_ease_forwards]">
        <header className="mb-6">
          <div className="mb-3">
            <span
              className="inline-block px-3 py-1 rounded-full text-xs font-bold capitalize"
              style={{ background: 'color-mix(in oklab, var(--accent) 10%, transparent)', color: 'var(--accent)' }}
            >
              {KATEGORI_LABEL[cat] || cat}
            </span>
          </div>

          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white leading-tight mb-4">
            {item.judul}
          </h1>

          <div className="flex items-center gap-4 text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 flex-wrap">
            <span className="flex items-center gap-1.5 font-medium text-neutral-700 dark:text-neutral-300">
              <span className="material-symbols-outlined text-base">groups</span> Exotic Pet World
            </span>
            {dateFull && (
              <span className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-base">calendar_today</span> {dateFull}
              </span>
            )}
          </div>
        </header>

        <div
          onClick={() => { setLightboxImg(img); setLightboxOpen(true); }}
          className="mb-6 rounded-xl overflow-hidden shadow-sm aspect-[20/9] w-full cursor-pointer group relative"
        >
          <img src={img} alt={item.judul} onError={handleImageError} className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.02]" />
          <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <span className="material-symbols-outlined text-white bg-black/40 p-3 rounded-full backdrop-blur-sm">zoom_in</span>
          </div>
        </div>

        <div className="prose-article text-neutral-800 dark:text-neutral-200 leading-relaxed text-base sm:text-lg mb-8">
          {item.deskripsi && (
            <p className="text-lg font-semibold text-neutral-900 dark:text-white mb-6 border-l-4 pl-4" style={{ borderColor: 'var(--accent)' }}>
              {item.deskripsi}
            </p>
          )}
          <div dangerouslySetInnerHTML={{ __html: kontenHtml }} />
        </div>

        {/* Galeri Satwa / Dokumentasi Tambahan */}
        <div className="mt-10 pt-8 border-t border-neutral-100 dark:border-neutral-800">
          <h3 className="font-bold text-lg text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-forest-500">photo_library</span>
            Dokumentasi & Galeri Kegiatan
          </h3>
          {galleryImages === undefined ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="aspect-video rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
              <div className="aspect-video rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
              <div className="aspect-video rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
            </div>
          ) : galleryImages.length === 0 ? (
            <p className="text-center text-neutral-500 dark:text-neutral-400 py-4">Belum ada dokumentasi gambar untuk artikel ini.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {galleryImages.map((g) => (
                <div key={g.id} className="aspect-video rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 group relative cursor-pointer" onClick={() => { setLightboxImg(getImageUrl(g.image)); setLightboxOpen(true); }}>
                  <img src={getImageUrl(g.image)} alt={g.caption || `Dokumentasi ${g.id}`} onError={handleImageError} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              ))}
            </div>
          )}
        </div>
      </article>

      {lightboxOpen && (
        <ImageLightbox img={lightboxImg || img} title={item.judul} onClose={() => { setLightboxOpen(false); setLightboxImg(null); }} />
      )}
    </>
  );
}

function ImageLightbox({ img, title, onClose }) {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const dragState = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const zoomIn = () => setScale((s) => Math.min(4, s + 0.25));
  const zoomOut = () => setScale((s) => Math.max(0.5, s - 0.25));
  const resetZoom = () => { setScale(1); setTranslate({ x: 0, y: 0 }); };

  const onPointerDown = (e) => {
    if (scale === 1) return;
    const point = e.touches ? e.touches[0] : e;
    dragState.current = { startX: point.clientX - translate.x, startY: point.clientY - translate.y };
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const point = e.touches ? e.touches[0] : e;
    setTranslate({ x: point.clientX - dragState.current.startX, y: point.clientY - dragState.current.startY });
  };
  const onPointerUp = () => { dragState.current = null; };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-neutral-950/95 backdrop-blur-md flex flex-col justify-between p-4 select-none"
      onMouseMove={onPointerMove}
      onMouseUp={onPointerUp}
      onTouchMove={onPointerMove}
      onTouchEnd={onPointerUp}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex justify-between items-center w-full max-w-7xl mx-auto p-4 rounded-2xl bg-white/95 dark:bg-neutral-900/90 text-neutral-800 dark:text-white border border-neutral-200/50 dark:border-neutral-800 shadow-xl">
        <div className="truncate pr-4">
          <h3 className="font-bold text-sm sm:text-base truncate">{title}</h3>
          <p className="text-xs text-neutral-400 dark:text-neutral-500">Media Preview</p>
        </div>
        <button onClick={onClose} className="w-10 h-10 rounded-xl flex items-center justify-center bg-neutral-100 hover:bg-neutral-200 dark:bg-white/10 dark:hover:bg-white/20 hover:rounded-2xl active:rounded-lg active:scale-95 transition-all duration-300 text-neutral-800 dark:text-white" title="Tutup">
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="flex-grow flex items-center justify-center overflow-hidden my-4 relative">
        <div
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
          className="transition-transform duration-200 ease-out cursor-grab active:cursor-grabbing"
          style={{ transform: `scale(${scale}) translate(${translate.x}px, ${translate.y}px)` }}
        >
          <img src={img} alt="Preview" className="max-w-full max-h-[65vh] sm:max-h-[70vh] object-contain rounded-lg shadow-2xl pointer-events-none" />
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 bg-white/95 dark:bg-neutral-900/90 text-neutral-800 dark:text-white border border-neutral-200/50 dark:border-neutral-800 backdrop-blur px-6 py-3 rounded-2xl w-fit mx-auto shadow-xl mb-4">
        <button onClick={zoomOut} className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-white/10 hover:rounded-2xl active:rounded-lg active:scale-95 transition-all duration-300" title="Perkecil">
          <span className="material-symbols-outlined">zoom_out</span>
        </button>
        <button onClick={resetZoom} className="px-3 py-1 text-xs font-semibold rounded-lg bg-neutral-100 dark:bg-white/10 hover:bg-neutral-200 dark:hover:bg-white/20 hover:rounded-xl active:rounded-md active:scale-95 transition-all duration-300" title="Reset Ukuran">
          {Math.round(scale * 100)}%
        </button>
        <button onClick={zoomIn} className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-white/10 hover:rounded-2xl active:rounded-lg active:scale-95 transition-all duration-300" title="Perbesar">
          <span className="material-symbols-outlined">zoom_in</span>
        </button>
        <div className="w-px h-6 bg-neutral-200 dark:bg-neutral-800" />
        <a href={img} download className="w-10 h-10 rounded-xl flex items-center justify-center bg-forest-500 hover:bg-forest-400 dark:bg-forest-600 dark:hover:bg-forest-500 hover:rounded-2xl active:rounded-lg active:scale-95 transition-all duration-300 !text-white" title="Simpan Foto">
          <span className="material-symbols-outlined">download</span>
        </a>
      </div>
    </div>
  );
}

function ShareCard({ item, shareUrl }) {
  const [copied, setCopied] = useState(false);
  const judul = item.judul;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => console.error('Gagal menyalin link:', err));
  };

  const links = [
    { name: 'WhatsApp', bg: '#25D366', href: `https://wa.me/?text=${encodeURIComponent(judul + ' - ' + shareUrl)}`,
      icon: <path fillRule="evenodd" clipRule="evenodd" d="M3.50002 12C3.50002 7.30558 7.3056 3.5 12 3.5C16.6944 3.5 20.5 7.30558 20.5 12C20.5 16.6944 16.6944 20.5 12 20.5C10.3278 20.5 8.77127 20.0182 7.45798 19.1861C7.21357 19.0313 6.91408 18.9899 6.63684 19.0726L3.75769 19.9319L4.84173 17.3953C4.96986 17.0955 4.94379 16.7521 4.77187 16.4751C3.9657 15.176 3.50002 13.6439 3.50002 12ZM12 1.5C6.20103 1.5 1.50002 6.20101 1.50002 12C1.50002 13.8381 1.97316 15.5683 2.80465 17.0727L1.08047 21.107C0.928048 21.4637 0.99561 21.8763 1.25382 22.1657C1.51203 22.4552 1.91432 22.5692 2.28599 22.4582L6.78541 21.1155C8.32245 21.9965 10.1037 22.5 12 22.5C17.799 22.5 22.5 17.799 22.5 12C22.5 6.20101 17.799 1.5 12 1.5ZM14.2925 14.1824L12.9783 15.1081C12.3628 14.7575 11.6823 14.2681 10.9997 13.5855C10.2901 12.8759 9.76402 12.1433 9.37612 11.4713L10.2113 10.7624C10.5697 10.4582 10.6678 9.94533 10.447 9.53028L9.38284 7.53028C9.23954 7.26097 8.98116 7.0718 8.68115 7.01654C8.38113 6.96129 8.07231 7.046 7.84247 7.24659L7.52696 7.52195C6.76823 8.18414 6.3195 9.2723 6.69141 10.3741C7.07698 11.5163 7.89983 13.314 9.58552 14.9997C11.3991 16.8133 13.2413 17.5275 14.3186 17.8049C15.1866 18.0283 16.008 17.7288 16.5868 17.2572L17.1783 16.7752C17.4313 16.5691 17.5678 16.2524 17.544 15.9269C17.5201 15.6014 17.3389 15.308 17.0585 15.1409L15.3802 14.1409C15.0412 13.939 14.6152 13.9552 14.2925 14.1824Z" />,
      viewBox: '0 0 24 24' },
    { name: 'Facebook', bg: '#1877F2', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      icon: <path d="M335.821282,7259 L335.821282,7250 L338.553693,7250 L339,7246 L335.821282,7246 L335.821282,7244.052 C335.821282,7243.022 335.847593,7242 337.286884,7242 L338.744689,7242 L338.744689,7239.14 C338.744689,7239.097 337.492497,7239 336.225687,7239 C333.580004,7239 331.923407,7240.657 331.923407,7243.7 L331.923407,7246 L329,7246 L329,7250 L331.923407,7250 L331.923407,7259 L335.821282,7259 Z" transform="translate(-329.000000, -7239.000000)" />,
      viewBox: '-5 0 20 20' },
    { name: 'Instagram', bg: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', href: 'https://www.instagram.com/',
      icon: <path d="M289.869652,7279.12273 C288.241769,7279.19618 286.830805,7279.5942 285.691486,7280.72871 C284.548187,7281.86918 284.155147,7283.28558 284.081514,7284.89653 C284.035742,7285.90201 283.768077,7293.49818 284.544207,7295.49028 C285.067597,7296.83422 286.098457,7297.86749 287.454694,7298.39256 C288.087538,7298.63872 288.809936,7298.80547 289.869652,7298.85411 C298.730467,7299.25511 302.015089,7299.03674 303.400182,7295.49028 C303.645956,7294.859 303.815113,7294.1374 303.86188,7293.08031 C304.26686,7284.19677 303.796207,7282.27117 302.251908,7280.72871 C301.027016,7279.50685 299.5862,7278.67508 289.869652,7279.12273 M289.951245,7297.06748 C288.981083,7297.0238 288.454707,7296.86201 288.103459,7296.72603 C287.219865,7296.3826 286.556174,7295.72155 286.214876,7294.84312 C285.623823,7293.32944 285.819846,7286.14023 285.872583,7284.97693 C285.924325,7283.83745 286.155174,7282.79624 286.959165,7281.99226 C287.954203,7280.99968 289.239792,7280.51332 297.993144,7280.90837 C299.135448,7280.95998 300.179243,7281.19026 300.985224,7281.99226 C301.980262,7282.98483 302.473801,7284.28014 302.071806,7292.99991 C302.028024,7293.96767 301.865833,7294.49274 301.729513,7294.84312 C300.829003,7297.15085 298.757333,7297.47145 289.951245,7297.06748 M298.089663,7283.68956 C298.089663,7284.34665 298.623998,7284.88065 299.283709,7284.88065 C299.943419,7284.88065 300.47875,7284.34665 300.47875,7283.68956 C300.47875,7283.03248 299.943419,7282.49847 299.283709,7282.49847 C298.623998,7282.49847 298.089663,7283.03248 298.089663,7283.68956 M288.862673,7288.98792 C288.862673,7291.80286 291.150266,7294.08479 293.972194,7294.08479 C296.794123,7294.08479 299.081716,7291.80286 299.081716,7288.98792 C299.081716,7286.17298 296.794123,7283.89205 293.972194,7283.89205 C291.150266,7283.89205 288.862673,7286.17298 288.862673,7288.98792 M290.655732,7288.98792 C290.655732,7287.16159 292.140329,7285.67967 293.972194,7285.67967 C295.80406,7285.67967 297.288657,7287.16159 297.288657,7288.98792 C297.288657,7290.81525 295.80406,7292.29716 293.972194,7292.29716 C292.140329,7292.29716 290.655732,7290.81525 290.655732,7288.98792" transform="translate(-284.000000, -7279.000000)" />,
      viewBox: '0 0 20 20' },
    { name: 'Threads', bg: '#000000', href: `https://www.threads.net/intent/post?text=${encodeURIComponent(judul + ' - ' + shareUrl)}`,
      icon: <path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z" />,
      viewBox: '0 0 192 192' },
    { name: 'X', bg: '#000000', href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(judul)}`,
      icon: <path d="m236 0h46l-101 115 118 156h-92.6l-72.5-94.8-83 94.8h-46l107-123-113-148h94.9l65.5 86.6zm-16.1 244h25.5l-165-218h-27.4z" />,
      viewBox: '0 0 300 271' },
    { name: 'Bluesky', bg: '#1185fe', href: `https://bsky.app/intent/compose?text=${encodeURIComponent(judul + ' ' + shareUrl)}`,
      icon: <path d="m135.72 44.03c66.496 49.921 138.02 151.14 164.28 205.46 26.262-54.316 97.782-155.54 164.28-205.46 47.98-36.021 125.72-63.892 125.72 24.795 0 17.712-10.155 148.79-16.111 170.07-20.703 73.984-96.144 92.854-163.25 81.433 117.3 19.964 147.14 86.092 82.697 152.22-122.39 125.59-175.91-31.511-189.63-71.766-2.514-7.3797-3.6904-10.832-3.7077-7.8964-0.0174-2.9357-1.1937 0.51669-3.7077 7.8964-13.714 40.255-67.233 197.36-189.63 71.766-64.444-66.128-34.605-132.26 82.697-152.22-67.108 11.421-142.55-7.4491-163.25-81.433-5.9562-21.282-16.111-152.36-16.111-170.07 0-88.687 77.742-60.816 125.72-24.795z" />,
      viewBox: '0 0 600 530' },
  ];

  return (
    <div className="p-6 rounded-2xl border bg-white dark:bg-neutral-900 shadow-sm mb-6" style={{ borderColor: 'color-mix(in oklab, var(--accent) 15%, transparent)' }}>
      <div className="flex items-center gap-3 mb-5 border-b border-neutral-100 dark:border-neutral-800 pb-4">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ background: 'var(--accent)' }}>
          <span className="material-symbols-outlined">verified_user</span>
        </div>
        <div>
          <h4 className="font-bold text-sm text-neutral-900 dark:text-white">Exotic Pet World</h4>
          <p className="text-xs text-neutral-400">Official Media</p>
        </div>
      </div>

      <div className="space-y-3">
        <span className="text-xs font-bold uppercase tracking-wider text-neutral-400 block">Bagikan Artikel Ini</span>
        <div className="flex flex-wrap items-center gap-3">
          {links.map((l) => (
            <a key={l.name} target="_blank" rel="noreferrer" href={l.href} title={`Bagikan ke ${l.name}`}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rounded-2xl active:rounded-3xl active:scale-95 !text-white"
              style={{ background: l.bg }}>
              <svg className="w-5 h-5 fill-current text-white" viewBox={l.viewBox} xmlns="http://www.w3.org/2000/svg">{l.icon}</svg>
            </a>
          ))}
          <button onClick={copyLink} title="Salin Tautan"
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rounded-2xl active:rounded-3xl active:scale-95 text-white relative"
            style={{ background: '#6B7280' }}>
            {copied && <span className="absolute -top-8 bg-black text-white text-xs px-2 py-1 rounded shadow-md">Tersalin!</span>}
            <svg className="w-5 h-5 fill-none stroke-current text-white" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function RelatedCard({ related }) {
  return (
    <div className="p-5 rounded-2xl border bg-white dark:bg-neutral-900 shadow-sm" style={{ borderColor: 'color-mix(in oklab, var(--accent) 15%, transparent)' }}>
      <h3 className="font-bold text-sm text-neutral-900 dark:text-white mb-4 flex items-center gap-2 border-b border-neutral-100 dark:border-neutral-800 pb-2">
        <span className="w-1.5 h-4 rounded-sm inline-block" style={{ backgroundColor: 'var(--accent)' }} />
        Berita Terbaru
      </h3>

      {related.length > 0 ? (
        <div className="space-y-4">
          {related.map((r) => (
            <Link key={r.id} to={`/artikel/${r.id}`} className="flex gap-3 items-center group border-b border-neutral-50 dark:border-neutral-800/40 pb-3 last:border-none last:pb-0">
              <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-neutral-100">
                <img src={resolveImage(r)} alt={r.judul} onError={handleImageError} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="flex-grow">
                <h4 className="text-xs sm:text-sm font-bold text-neutral-800 dark:text-neutral-200 line-clamp-2 leading-snug group-hover:text-forest-500 transition-colors">
                  {r.judul}
                </h4>
                {r.tanggal && <span className="text-[10px] text-neutral-400 block mt-1">{formatDateShort(r.tanggal)}</span>}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="py-2">
          <h4 className="text-xs font-medium text-neutral-400 dark:text-neutral-500">Belum ada artikel terkait lainnya saat ini.</h4>
        </div>
      )}
    </div>
  );
}
